import type { ISettings } from 'types/models/ISettings';
import enTranslations from '../../../../assets/public/assets/portal/translations/en.json';
import { useAppStore } from '../../store/app/appStore';

export const buildPortalAssetUrl = (path: string): string => {
  const { assetsHost, assetsHash } = useAppStore.getState().settings;

  if (!assetsHost || assetsHost === '' || !path) {
    return '';
  }

  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  const normalizedHost = assetsHost.replace(/\/$/, '');
  const hash = (assetsHash || '').trim();
  const rootPath = hash ? `/${hash}/assets/portal` : '/assets/portal';
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${normalizedHost}${rootPath}${normalizedPath}`;

  return hash ? `${url}?hash=${encodeURIComponent(hash)}` : url;
};

export const formatNumberWithSpaces = (num: string | number): string => {
  return (num ?? NaN).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

export const formatCurrencyValue = (value: number | string, divisor: number = 1): string => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  const formatted = numValue / divisor;
  return formatted.toFixed(2);
};

export const formatCurrencyValueWithSpaces = (value: number, fixedDecimals = false): string => {
  const formattedValue = !fixedDecimals && Number.isSafeInteger(value) ? value : formatCurrencyValue(value);

  return formatNumberWithSpaces(formattedValue);
};

export const asset = (settings: ISettings, value: string) => {
  const hash = settings.assetsHash;
  const assetUri = `${settings.assetsHost}${value.replace('assets/', `${hash ? hash + '/' : ''}assets/`)}?hash=${
    settings.assetsHash
  }`;

  return assetUri;
};

/**
 * Finds a nested translation value in the provided JSON object.
 * @param keyPath - Dot-separated path to the translation key (e.g. "tournaments.rules.spins_win_rule").
 * @param json - Optional JSON translations object. Defaults to English translations if not provided.
 * @returns The translation string if found, otherwise null.
 */
type TranslationTree = { [key: string]: string | TranslationTree };

export const findTranslationKeyAsGivenJSONTranslationFallback = (
  keyPath: string,
  json: TranslationTree = enTranslations,
): string => {
  const value = keyPath.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') {
      return (acc as TranslationTree)[key] || null;
    }
    return null;
  }, json);

  return value as string;
};
